// Pure helpers for the owner-only Outreach board: grouped filters, due-first
// sort and the one-line "next step" copy. No API shape changes; the board
// rows come straight from GET /outreach/board (backend/outreach.py).

export const OPEN_STATUSES = ['queued', 'active'];
export const CLOSED_STATUSES = ['halted', 'suppressed', 'converted', 'lost'];

export const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'due', label: 'Due' },
  { key: 'active', label: 'Active' },
  { key: 'replied', label: 'Replied' },
  { key: 'closed', label: 'Closed' },
];

export const SEGMENTS = [
  { key: 'all', label: 'All segments' },
  { key: 'brokerage', label: 'Brokerage' },
  { key: 'team', label: 'Team' },
  { key: 'agent', label: 'Agent' },
];

const SEGMENT_LABELS = { brokerage: 'Brokerage', team: 'Team', agent: 'Agent' };

const STATUS_LABELS = {
  queued: 'Queued',
  active: 'Active',
  replied: 'Replied',
  halted: 'Halted',
  suppressed: 'Suppressed',
  converted: 'Converted',
  lost: 'Lost',
};

/** Local calendar date as YYYY-MM-DD (not UTC, so evenings in Toronto stay "today"). */
export function localToday(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Date-only strings are parsed as local dates so "2026-10-26" never shows as Oct 25. */
export function parseDay(d) {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function shortDay(d, now = new Date()) {
  const dt = parseDay(d);
  if (!dt) return '';
  const opts = { month: 'short', day: 'numeric' };
  if (dt.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return dt.toLocaleDateString('en-CA', opts);
}

export function segmentKey(row) {
  return String(row?.segment || '').toLowerCase();
}

export function segmentLabel(row) {
  return SEGMENT_LABELS[segmentKey(row)] || '';
}

export function isOpen(row) {
  return OPEN_STATUSES.includes(row?.status) && !row?.sequence_halted;
}

export function isDue(row, today = localToday()) {
  return isOpen(row) && !!row.next_touch_no && !!row.next_due && String(row.next_due).slice(0, 10) <= today;
}

export function isClosed(row) {
  return CLOSED_STATUSES.includes(row?.status) || (!!row?.sequence_halted && row?.status !== 'replied');
}

export function matchesFilter(row, key, today = localToday()) {
  switch (key) {
    case 'due': return isDue(row, today);
    case 'active': return isOpen(row);
    case 'replied': return row?.status === 'replied';
    case 'closed': return isClosed(row);
    default: return true;
  }
}

export function matchesSearch(row, q) {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return true;
  return [row.name, row.contact_person, row.email, row.instagram]
    .some((v) => String(v || '').toLowerCase().includes(s));
}

/** One quiet status word per row. */
export function statusLabel(row, today = localToday()) {
  if (isDue(row, today)) return String(row.next_due).slice(0, 10) < today ? 'Overdue' : 'Due today';
  if (row?.sequence_halted && row?.status !== 'replied' && !CLOSED_STATUSES.includes(row?.status)) return 'Halted';
  return STATUS_LABELS[row?.status] || 'Queued';
}

export function channelLabel(row) {
  return row?.channel === 'email' ? 'Email' : 'Instagram';
}

/** Secondary line: contact · address/handle · segment. */
export function contactLine(row) {
  const dest = row?.channel === 'email' ? row?.email : (row?.instagram ? `@${row.instagram}` : null);
  return [row?.contact_person, dest, segmentLabel(row)].filter(Boolean).join(' · ');
}

/**
 * Next-step line split into parts so the page can set the due date in mono.
 * Returns [{ text, figure? }].
 */
export function nextStepParts(row, now = new Date()) {
  const total = row?.touches_total || 5;
  const sent = row?.touches_sent || 0;
  const parts = [{ text: channelLabel(row) }];
  if (row?.status === 'replied') {
    const n = row.reply_count || 1;
    parts.push({ text: `${n} ${n === 1 ? 'reply' : 'replies'}` });
    parts.push({ text: `${sent} of ${total} sent` });
    return parts;
  }
  if (isClosed(row)) {
    parts.push({ text: `${sent} of ${total} sent` });
    return parts;
  }
  if (!row?.next_touch_no) {
    parts.push({ text: 'Sequence complete' });
    return parts;
  }
  parts.push({ text: `Touch ${row.next_touch_no} of ${total}` });
  if (row.next_due) parts.push({ text: shortDay(row.next_due, now), figure: true });
  return parts;
}

const RANK = (row, today) => {
  if (isDue(row, today)) return 0;
  if (isOpen(row)) return 1;
  if (row?.status === 'replied') return 2;
  return 3;
};

/** Due first (oldest due date first), then open by due date, replied, closed; ties by name. */
export function sortRows(rows, today = localToday()) {
  return [...(rows || [])].sort((a, b) => {
    const ra = RANK(a, today), rb = RANK(b, today);
    if (ra !== rb) return ra - rb;
    const da = a.next_due || '9999-12-31', db = b.next_due || '9999-12-31';
    if (ra <= 1 && da !== db) return da < db ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

export function emptyMessage(filterKey, { search = '', segment = 'all' } = {}) {
  if (String(search).trim()) return 'No prospects match that search.';
  const base = {
    due: 'Nothing due today.',
    active: 'No active sequences.',
    replied: 'No replies yet.',
    closed: 'Nothing closed yet.',
    all: 'No prospects yet.',
  }[filterKey] || 'No prospects yet.';
  if (segment && segment !== 'all') return base.replace(/\.$/, ` in ${SEGMENT_LABELS[segment] || segment}.`);
  return base;
}
